import React, { FC, useState, useRef } from 'react'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'
import { Badge } from 'primereact/badge'
import { PageTitle } from '../../../../../_designereyes/layout/core'
import { useThemeMode } from '../../../../../_designereyes/partials/layout/theme-mode/ThemeModeProvider'
import './DataReformatter.css'

const DataReformatterWrapper: FC = () => {
  const [inputData, setInputData] = useState<string>('')
  const [outputData, setOutputData] = useState<string>('')
  const [itemCount, setItemCount] = useState<number>(0)
  const [duplicateCount, setDuplicateCount] = useState<number>(0)
  const toastRef = useRef<Toast>(null)
  
  // Get current theme
  const { mode } = useThemeMode()
  const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  const currentTheme = mode === 'system' ? systemMode : mode

  // Parse input data and update item count
  const parseInputData = (data: string): string[] => {
    const items = data.trim().split(/\s+/).filter(item => item.length > 0)
    setItemCount(items.length)
    
    // Count duplicates
    const uniqueItems = Array.from(new Set(items))
    setDuplicateCount(items.length - uniqueItems.length)
    
    return items
  }

  // Remove duplicates from input
  const removeDuplicates = () => {
    const items = parseInputData(inputData)
    if (items.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Please enter some UPCs/SKUs first',
        life: 3000,
      })
      return
    }

    const uniqueItems = Array.from(new Set(items))
    const removedCount = items.length - uniqueItems.length
    
    if (removedCount === 0) {
      toastRef.current?.show({
        severity: 'info',
        summary: 'No Duplicates Found',
        detail: 'Your data already contains unique items only',
        life: 3000,
      })
      return
    }

    const cleanedData = uniqueItems.join('\n')
    setInputData(cleanedData)
    parseInputData(cleanedData)
    
    toastRef.current?.show({
      severity: 'success',
      summary: 'Duplicates Removed',
      detail: `Removed ${removedCount} duplicate${removedCount > 1 ? 's' : ''}`,
      life: 3000,
    })
  }

  // Convert to NAV format (pipe-separated)
  const convertToNav = () => {
    const items = parseInputData(inputData)
    if (items.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Please enter some UPCs/SKUs to convert',
        life: 3000,
      })
      return
    }
    
    const result = items.join('|')
    setOutputData(result)
    
    toastRef.current?.show({
      severity: 'success',
      summary: 'Converted to NAV Format',
      detail: `${items.length} items converted successfully`,
      life: 3000,
    })
  }

  // Convert to SQL format (quoted, comma-separated in parentheses)
  const convertToSql = () => {
    const items = parseInputData(inputData)
    if (items.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Please enter some UPCs/SKUs to convert',
        life: 3000,
      })
      return
    }
    
    const result = "('" + items.join("','") + "')"
    setOutputData(result)
    
    toastRef.current?.show({
      severity: 'success',
      summary: 'Converted to SQL Format',
      detail: `${items.length} items converted successfully`,
      life: 3000,
    })
  }

  // Convert to semicolon format
  const convertToSemicolon = () => {
    const items = parseInputData(inputData)
    if (items.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'No Data',
        detail: 'Please enter some UPCs/SKUs to convert',
        life: 3000,
      })
      return
    }
    
    const result = items.join(';')
    setOutputData(result)
    
    toastRef.current?.show({
      severity: 'success',
      summary: 'Converted to Semicolon Format',
      detail: `${items.length} items converted successfully`,
      life: 3000,
    })
  }

  // Copy output to clipboard
  const copyToClipboard = async () => {
    if (!outputData) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Nothing to Copy',
        detail: 'No output data available to copy',
        life: 3000,
      })
      return
    }

    try {
      await navigator.clipboard.writeText(outputData)
      toastRef.current?.show({
        severity: 'success',
        summary: 'Copied!',
        detail: 'Output copied to clipboard successfully',
        life: 2000,
      })
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = outputData
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Copied!',
        detail: 'Output copied to clipboard successfully',
        life: 2000,
      })
    }
  }

  // Clear all data
  const clearAll = () => {
    setInputData('')
    setOutputData('')
    setItemCount(0)
    setDuplicateCount(0)
    toastRef.current?.show({
      severity: 'info',
      summary: 'Cleared',
      detail: 'All data has been cleared',
      life: 2000,
    })
  }

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setInputData(value)
    parseInputData(value)
  }

  return (
    <div className={`data-reformatter-container data-reformatter-${currentTheme}`}>
      <Toast ref={toastRef} position="bottom-right" />
      
      <div className="container-fluid px-4">
        <div className="text-center mb-5">
          <div className="data-reformatter-title">
            <PageTitle>
              <i className="fa-solid fa-arrows-rotate me-3 text-primary"></i>
              Data Reformatter
            </PageTitle>
          </div>
          <p className="data-reformatter-subtitle">Transform your UPC/SKU data into different formats with ease</p>
        </div>
        
        <div className="row g-4 mb-5">
          {/* Input Section */}
          <div className="col-lg-6">
            <Card className="h-100 shadow-lg border-0 data-reformatter-modern-card">
              <div className="card-header data-reformatter-bg-gradient-primary text-white border-0 d-flex justify-content-between align-items-center data-reformatter-card-header">
                <h5 className="card-title mb-0 fw-bold">
                  <i className="fa-solid fa-file-import me-2"></i>
                  Input Data
                </h5>
                <div className="d-flex gap-2">
                  {itemCount > 0 && (
                    <Badge value={`${itemCount} items`} severity="info" className="data-reformatter-item-count-badge" />
                  )}
                  {duplicateCount > 0 && (
                    <Badge value={`${duplicateCount} duplicates`} severity="warning" className="data-reformatter-item-count-badge" />
                  )}
                </div>
              </div>
              
              <div className="card-body p-4">
                <div className="mb-4">
                  <label className="form-label fw-semibold data-reformatter-text-muted mb-3">
                    <i className="fa-solid fa-paste me-2 text-primary"></i>
                    Paste UPCs/SKUs here (one per line or space-separated):
                  </label>
                  <InputTextarea
                    value={inputData}
                    onChange={handleInputChange}
                    placeholder="Enter your UPCs or SKUs here...&#10;Example:&#10;123456789012&#10;987654321098&#10;555666777888"
                    rows={12}
                    className="w-100 data-reformatter-monospace-input data-reformatter-modern-textarea"
                  />
                </div>
                
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    label="Clear All"
                    icon="pi pi-trash"
                    className="btn btn-outline-danger data-reformatter-btn-modern"
                    onClick={clearAll}
                    disabled={!inputData}
                  />
                  <Button
                    label="Remove Duplicates"
                    icon="pi pi-filter"
                    className="btn btn-outline-warning data-reformatter-btn-modern"
                    onClick={removeDuplicates}
                    disabled={!inputData || duplicateCount === 0}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Output Section */}
          <div className="col-lg-6">
            <Card className="h-100 shadow-lg border-0 data-reformatter-modern-card">
              <div className="card-header data-reformatter-bg-gradient-success text-white border-0 data-reformatter-card-header">
                <h5 className="card-title mb-0 fw-bold">
                  <i className="fa-solid fa-file-export me-2"></i>
                  Converted Output
                </h5>
              </div>
              
              <div className="card-body p-4">
                <div className="mb-4">
                  <label className="form-label fw-semibold data-reformatter-text-muted mb-3">
                    <i className="fa-solid fa-magic me-2 text-success"></i>
                    Formatted Result:
                  </label>
                  <InputTextarea
                    value={outputData}
                    readOnly
                    placeholder="Converted data will appear here..."
                    rows={10}
                    className="w-100 data-reformatter-monospace-input data-reformatter-modern-textarea"
                  />
                </div>

                <div className="d-grid">
                  <Button
                    label="Copy to Clipboard"
                    icon="pi pi-copy"
                    className="btn btn-success data-reformatter-btn-modern btn-lg"
                    onClick={copyToClipboard}
                    disabled={!outputData}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Conversion Buttons Section */}
        <Card className="shadow-lg border-0 data-reformatter-modern-card mb-5">
          <div className="card-header data-reformatter-bg-gradient-info text-white text-center data-reformatter-card-header">
            <h4 className="card-title mb-0 fw-bold">
              <i className="fa-solid fa-arrows-rotate me-2"></i>
              Format Converters
            </h4>
            <p className="mb-0 mt-2 opacity-75">Choose your desired output format</p>
          </div>
          
          <div className="card-body p-5">
            <div className="row g-4">
              {/* NAV Format */}
              <div className="col-lg-4 col-md-6">
                <div className="data-reformatter-format-converter-card data-reformatter-card-hover-effect h-100 text-center p-4">
                  <div className="data-reformatter-format-bg-icon">
                    <i className="fab fa-microsoft"></i>
                  </div>
                  <div className="position-relative">
                    <div className="mb-3">
                      <i className="fab fa-microsoft text-info data-reformatter-format-icon"></i>
                    </div>
                    <h5 className="fw-bold mb-2 data-reformatter-text-dark">NAV Format</h5>
                    <p className="data-reformatter-text-muted small mb-3">
                      Pipe-separated values for Microsoft NAV systems
                    </p>
                    <div className="data-reformatter-format-example mb-4">
                      <code className="text-info">
                        123456|789012|345678
                      </code>
                    </div>
                    <Button
                      label="Convert to NAV"
                      icon="pi pi-arrow-right"
                      className="btn btn-info data-reformatter-btn-modern w-100"
                      onClick={convertToNav}
                      disabled={!inputData.trim()}
                    />
                  </div>
                </div>
              </div>

              {/* SQL Format */}
              <div className="col-lg-4 col-md-6">
                <div className="data-reformatter-format-converter-card data-reformatter-card-hover-effect h-100 text-center p-4">
                  <div className="data-reformatter-format-bg-icon">
                    <i className="fa-solid fa-database"></i>
                  </div>
                  <div className="position-relative">
                    <div className="mb-3">
                      <i className="fa-solid fa-database text-warning data-reformatter-format-icon"></i>
                    </div>
                    <h5 className="fw-bold mb-2 data-reformatter-text-dark">SQL Format</h5>
                    <p className="data-reformatter-text-muted small mb-3">
                      SQL IN clause format with quotes
                    </p>
                    <div className="data-reformatter-format-example mb-4">
                      <code className="text-warning">
                        ('123456','789012','345678')
                      </code>
                    </div>
                    <Button
                      label="Convert to SQL"
                      icon="pi pi-arrow-right"
                      className="btn btn-warning data-reformatter-btn-modern w-100"
                      onClick={convertToSql}
                      disabled={!inputData.trim()}
                    />
                  </div>
                </div>
              </div>

              {/* Semicolon Format */}
              <div className="col-lg-4 col-md-6">
                <div className="data-reformatter-format-converter-card data-reformatter-card-hover-effect h-100 text-center p-4">
                  <div className="data-reformatter-format-bg-icon">
                    <i className="fa-solid fa-semicolon"></i>
                  </div>
                  <div className="position-relative">
                    <div className="mb-3">
                      <i className="fa-solid fa-list data-reformatter-text-purple data-reformatter-format-icon"></i>
                    </div>
                    <h5 className="fw-bold mb-2 data-reformatter-text-dark">Semicolon Format</h5>
                    <p className="data-reformatter-text-muted small mb-3">
                      Semicolon-separated values
                    </p>
                    <div className="data-reformatter-format-example mb-4">
                      <code className="data-reformatter-text-purple">
                        123456;789012;345678
                      </code>
                    </div>
                    <Button
                      label="Convert to Semicolon"
                      icon="pi pi-arrow-right"
                      className="data-reformatter-btn-purple data-reformatter-btn-modern w-100"
                      onClick={convertToSemicolon}
                      disabled={!inputData.trim()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Help Section */}
        <Card className="shadow-lg border-0 data-reformatter-modern-card">
          <div className="card-body p-4 data-reformatter-help-section">
            <h5 className="fw-bold mb-4 text-center data-reformatter-text-dark">
              <i className="fa-solid fa-circle-info me-2 text-info"></i>
              How to Use This Tool
            </h5>
            <div className="row g-4">
              <div className="col-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="data-reformatter-step-badge bg-primary text-white me-3">1</div>
                  <div>
                    <h6 className="mb-1 fw-bold data-reformatter-text-dark">Input Data</h6>
                    <small className="data-reformatter-text-muted">Paste your UPCs/SKUs in the input area</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="data-reformatter-step-badge bg-warning text-white me-3">2</div>
                  <div>
                    <h6 className="mb-1 fw-bold data-reformatter-text-dark">Clean Data</h6>
                    <small className="data-reformatter-text-muted">Remove duplicates if needed</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="data-reformatter-step-badge bg-info text-white me-3">3</div>
                  <div>
                    <h6 className="mb-1 fw-bold data-reformatter-text-dark">Convert</h6>
                    <small className="data-reformatter-text-muted">Choose your desired format</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="data-reformatter-step-badge bg-success text-white me-3">4</div>
                  <div>
                    <h6 className="mb-1 fw-bold data-reformatter-text-dark">Copy Result</h6>
                    <small className="data-reformatter-text-muted">Copy formatted data to clipboard</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export { DataReformatterWrapper }
